import {Fragment, useEffect, useState} from 'react';
import {Container} from '@/components/container';
import {
    Toolbar,
    ToolbarActions,
    ToolbarDescription,
    ToolbarHeading,
    ToolbarPageTitle,
} from '@/partials/toolbar';
import {PageNavbar} from '@/pages/account';
import {JobsContent} from './JobsContent';
import {JobModal} from '@/pages/buddy/job/blocks';
import {KeenIcon} from '@/components';
import axios from 'axios';
import {JobDetailsData} from '@/pages/buddy/types/JobDetailsData';
import {enqueueSnackbar} from "notistack";
import {NoJobsContent} from "@/pages/buddy/jobs/NoJobsContent";
import {useStatistic} from "@/contexts/StatisticContext";
import {NoCompaniesContent} from "@/pages/buddy/jobs";

const JobsPage = () => {
    const [jobs, setJobs] = useState<JobDetailsData[]>([]);
    const [newJobsModalOpen, setNewJobsModalOpen] = useState(false);
    const [loadingJobs, setLoadingJobs] = useState(true);
    const {statistic, refreshStatistic} = useStatistic();

    const handleOpen = () => setNewJobsModalOpen(true);
    const handleClose = () => setNewJobsModalOpen(false);

    const API_URL = import.meta.env.VITE_APP_API_URL;
    const API_JOBS = `${API_URL}/jobs`;

    const fetchJobsData = async () => {
        try {
            const response = await axios.get<JobDetailsData[]>(API_JOBS, {
                headers: {Accept: 'application/json'},
            });

            setJobs(response.data);

            // Auto-open modal if no jobs are returned
            if (response.data.length === 0) {
                handleOpen();
            }
        } catch (err) {
            enqueueSnackbar(`Failed to fetch jobs data: ${err}`, {
                variant: 'error',
            });
        } finally {
            setLoadingJobs(false);
        }
    };

    useEffect(() => {
        // Refresh statistics if not already loaded
        if (!statistic) {
            refreshStatistic();
        }
    }, [statistic, refreshStatistic]);

    useEffect(() => {
        // Once statistic is loaded and company_count > 0, fetch jobs
        if (statistic && statistic.company_count > 0) {
            fetchJobsData();
        }
    }, [statistic]);

    // While statistics are loading, render nothing or a loading indicator
    if (!statistic) {
        return <>emtpy</>;
    }

    // If no company exists, prompt the user to create one
    if (statistic.company_count === 0) {
        return (
            <Container>
                <NoCompaniesContent/>
            </Container>
        );
    }

    return (
        <Fragment>
            <PageNavbar/>

            <JobModal mode={"create"} open={newJobsModalOpen} onClose={handleClose}/>

            <Container>
                <Toolbar>
                    <ToolbarHeading>
                        <ToolbarPageTitle/>
                        <ToolbarDescription>
                            Efficient team organization with real-time updates
                        </ToolbarDescription>
                    </ToolbarHeading>
                    <ToolbarActions>
                        <button
                            onClick={handleOpen}
                            type="button"
                            className={`btn btn-sm btn-primary ${jobs.length === 0 ? 'pulsating' : ''}`}
                        >
                            <KeenIcon icon="plus"/> Add New Job Profile
                        </button>
                    </ToolbarActions>
                </Toolbar>
            </Container>

            <Container>
                {loadingJobs ? (
                    <div>Loading jobs...</div>
                ) : jobs.length === 0 ? (
                    <NoJobsContent onAddNewJob={handleOpen}/>
                ) : (
                    <JobsContent jobs={jobs} loading={loadingJobs}/>
                )}
            </Container>
        </Fragment>
    );
};

export {JobsPage};
